import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  MenuItem,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ButtonComp from "../../../../components/Button";
import Input from "../../../../components/Input";
import { useRelationOptions } from "../../../../hooks/useRelationOptions";
import { getCaseCreationPolicy, getPolicy } from "../../../../services/guest";
import { usePolicyStore } from "../../../../store/usePolicyStore";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import theme from "../../../../theme";
import {
  caseActionBarSx,
  caseButtonSx,
  caseFormCardSx,
  caseGridSx,
  caseRadioGroupSx,
} from "./caseFormStyles";

const getPolicyMemberValue = (member: any, keys: string[]) => {
  for (const key of keys) {
    const value = member?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const getPolicyMemberRelation = (member: any, fallback: string) => {
  const explicitRelation =
    getPolicyMemberValue(member, ["relationship", "relation", "BenRelation", "Relation"]) || "";
  if (explicitRelation) return explicitRelation;

  const memberRole = getPolicyMemberValue(member, ["casePortalMemberRole", "CasePortalMemberRole"]);
  if (memberRole === "MAIN_MEMBER") return "MAIN MEMBER";
  if (memberRole === "SPOUSE") return "SPOUSE";
  if (memberRole === "CHILD") return "CHILD";
  if (memberRole === "EXTENDED_FAMILY") return "EXTENDED FAMILY";
  if (/^spouse/i.test(fallback || "")) return "SPOUSE";
  if (/^child/i.test(fallback || "")) return "CHILD";
  if (/^main member/i.test(fallback || "")) return "MAIN MEMBER";
  if (/^extended family/i.test(fallback || "")) return "EXTENDED FAMILY";
  return fallback;
};

const getPolicyMemberIdentifier = (member: any) =>
  getPolicyMemberValue(member, [
    "identificationNumber", "idNumber", "idNum", "ID_Number", "ID_num",
    "passportNumber", "passportNum", "PassportNumber", "Passport_num",
  ]);

export default function DeceasedDetailsForm({
  deceasedDetails,
  setDeceasedDetails,
  onNext,
  onBack,
  minimalCapture = false,
  hideActions = false,
  validationRef,
}: any) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [policyError, setPolicyError] = useState("");
  const [policyLoading, setPolicyLoading] = useState(false);
  const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

  const { relationOptions, loading } = useRelationOptions();
  const { packageSelection, policyNumber, selectedLivesInsured } =
    useCaseCreationStore();
  const policyData = usePolicyStore((state) => state.policyData);
  const setPolicyData = usePolicyStore((state) => state.setPolicyData);
  const sourceType = minimalCapture ? "MANUAL" : packageSelection.sourceType;

  const formatPolicyDate = (value: any) => {
    if (!value) return "";
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    if (typeof value === "object") {
      const year = value.year || value.Year;
      const month = value.monthValue || value.month || value.Month;
      const day = value.dayOfMonth || value.day || value.Day;
      if (year && month && day) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
    const stringValue = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
      return stringValue.slice(0, 10);
    }
    const parsed = new Date(stringValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return "";
  };

  const getPolicyMemberName = (member: any) => {
    const title = getPolicyMemberValue(member, ["title", "Title"]);
    const firstName = getPolicyMemberValue(member, ["firstName", "name", "First_Name", "Name", "member_name"]);
    const lastName = getPolicyMemberValue(member, ["lastName", "surname", "Surname"]);
    return [title, firstName, lastName].filter(Boolean).join(" ").trim() || "Unnamed member";
  };

  const getExistingDeceasedSnapshot = (member: any) =>
    member?.casePortalExistingDeceased ||
    member?.CasePortalExistingDeceased ||
    null;

  const buildOptionsWithCurrentValue = (options: string[], currentValue: any) => {
    const normalizedCurrentValue = String(currentValue || "").trim();
    const normalizedOptions = options.filter(Boolean);

    if (!normalizedCurrentValue) {
      return normalizedOptions;
    }

    return normalizedOptions.includes(normalizedCurrentValue)
      ? normalizedOptions
      : [normalizedCurrentValue, ...normalizedOptions];
  };

  const normalizeTitleValue = (value: any) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const titleMap: Record<string, string> = {
      ADV: "Adv",
      DR: "Dr",
      MISS: "Miss",
      MR: "Mr",
      MRS: "Mrs",
      MS: "Ms",
      PROF: "Prof",
      REV: "Rev",
    };

    return titleMap[raw.toUpperCase()] || raw;
  };

  const normalizeMemberCollection = (...values: any[]) => {
    for (const value of values) {
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }
      if (value && typeof value === "object") {
        const memberKeys = [
          "Member_ID",
          "memberId",
          "Name",
          "name",
          "First_Name",
          "firstName",
          "Surname",
          "surname",
          "ID_num",
          "ID_Number",
          "idNum",
          "idNumber",
        ];
        const looksLikeSingleMember = memberKeys.some((key) => key in value);
        if (looksLikeSingleMember) {
          return [value];
        }

        const objectValues = Object.values(value).filter(
          (item) => item && typeof item === "object",
        );
        if (objectValues.length > 0) {
          return objectValues as any[];
        }
      }
    }
    return [];
  };

  const buildLivesInsuredOptions = () => {
    if (!policyData) return [];

    const options: Array<{ key: string; label: string; member: any }> = [];
    const pushMember = (member: any, relationFallback: string) => {
      if (!member) return;
      const key = `${relationFallback}-${options.length}`;
      options.push({
        key,
        label: [
          getPolicyMemberRelation(member, relationFallback),
          getPolicyMemberName(member),
          getPolicyMemberIdentifier(member),
        ]
          .filter(Boolean)
          .join(" · "),
        member: {
          ...member,
          __caseLivesInsuredKey: key,
          __caseLivesInsuredRelation: relationFallback,
        },
      });
    };

    const mainMember =
      policyData.mainMember || policyData.MainMember || policyData.main_member || null;
    if (mainMember) {
      pushMember(mainMember, "Main Member");
    } else if (policyData.member_name || policyData.member_contact_number) {
      pushMember(
        {
          firstName: policyData.member_name,
          contactNumber: policyData.member_contact_number,
          title: policyData.member_title,
          initials: policyData.member_initials,
        },
        "Main Member",
      );
    }

    normalizeMemberCollection(
      policyData.spouse,
      policyData.Spouse,
      policyData.spouses,
      policyData.spouseDetails,
      policyData.spouse_details,
    ).forEach((member, index) =>
      pushMember(member, `Spouse ${index + 1}`),
    );
    normalizeMemberCollection(
      policyData.children,
      policyData.Children,
      policyData.child,
      policyData.childrens,
      policyData.childrenDetails,
      policyData.children_details,
    ).forEach((member, index) =>
      pushMember(member, `Child ${index + 1}`),
    );
    normalizeMemberCollection(
      policyData.extendedFamily,
      policyData.ExtendedFamily,
      policyData.ExtendedFamiy,
      policyData.extended_family,
      policyData.dependents,
      policyData.dependentDetails,
      policyData.dependent_details,
    ).forEach(
      (member, index) => pushMember(member, getPolicyMemberRelation(member, `Extended Family ${index + 1}`)),
    );

    if (options.length === 0) {
      pushMember(
        {
          firstName: policyData.member_name || policyData.firstName || "",
          contactNumber: policyData.member_contact_number || policyData.cellNumber || "",
          title: policyData.member_title || policyData.title || "",
          initials: policyData.member_initials || policyData.initials || "",
        },
        "Main Member",
      );
    }

    return options;
  };

  const livesInsuredOptions = buildLivesInsuredOptions();
  const selectedLivesInsuredOption = selectedLivesInsured
    ? livesInsuredOptions.find(
        (option) =>
          option.key === selectedLivesInsured.__caseLivesInsuredKey ||
          getPolicyMemberIdentifier(option.member) === getPolicyMemberIdentifier(selectedLivesInsured),
      ) || null
    : null;
  const titleOptions = buildOptionsWithCurrentValue(
    ["Adv", "Dr", "Miss", "Mr", "Mrs", "Ms", "Prof", "Rev"],
    normalizeTitleValue(deceasedDetails.title),
  );
  const relationshipOptions = buildOptionsWithCurrentValue(
    relationOptions,
    deceasedDetails.relationship,
  );

  useEffect(() => {
    if (sourceType !== "POLICY") return;
    if (!policyNumber.trim() || policyData) return;

    let isMounted = true;

    const loadPolicy = async () => {
      setPolicyLoading(true);
      try {
        let response = await getCaseCreationPolicy("DEATH", policyNumber.trim());
        if (response?.status !== 200) {
          response = await getPolicy("DEATH", policyNumber.trim());
        }
        if (!isMounted) return;

        if (response?.status === 200 && response?.data) {
          const policy = response?.data?.data || response?.data;
          setPolicyData(policy);
          setPolicyError("");
        } else {
          setPolicyError(response?.data?.message || "Unable to load policy details");
        }
      } catch (error) {
        if (isMounted) {
          setPolicyError("Unable to load policy details");
        }
      } finally {
        if (isMounted) {
          setPolicyLoading(false);
        }
      }
    };

    loadPolicy();

    return () => {
      isMounted = false;
    };
  }, [policyData, policyNumber, setPolicyData, sourceType]);

  const extractSouthAfricanIdDetails = (id: string) => {
    if (!/^\d+$/.test(id) || id.length < 10) return null;

    const dob = id.substring(0, 6);
    const genderSequence = parseInt(id.substring(6, 10), 10);

    const year = parseInt(dob.substring(0, 2), 10);
    const month = parseInt(dob.substring(2, 4), 10);
    const day = parseInt(dob.substring(4, 6), 10);
    let fullYear = 2000 + year;
    if (new Date(fullYear, month - 1, day) > new Date()) {
      fullYear -= 100;
    }

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return null;
    }

    return {
      dateOfBirth: `${fullYear}-${String(month).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`,
      gender: genderSequence >= 5000 ? "Male" : "Female",
    };
  };

  const validateSouthAfricanID = (id: string): string | null => {
    // must be 13 digits
    if (!/^\d{13}$/.test(id)) return "ID number must be 13 digits";

    // Extract parts
    const dob = id.substring(0, 6);
    const ssss = parseInt(id.substring(6, 10), 10);
    const citizenship = parseInt(id.charAt(10), 10);
    // Validate DOB
    const year = parseInt(dob.substring(0, 2), 10);
    const month = parseInt(dob.substring(2, 4), 10);
    const day = parseInt(dob.substring(4, 6), 10);
    let fullYear = 2000 + year;
    if (new Date(fullYear, month - 1, day) > new Date()) {
      fullYear -= 100;
    }

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return "Invalid date of birth in ID number";
    }

    // Validate gender sequence
    if (ssss < 0 || ssss > 9999) {
      return "Invalid gender sequence in ID number";
    }

    // Validate citizenship
    if (![0, 1, 2].includes(citizenship)) {
      return "Invalid citizenship digit in ID number";
    }

    // Luhn checksum
    const luhnCheck = (num: string) => {
      let sum = 0;
      let alt = false;
      for (let i = num.length - 1; i >= 0; i--) {
        let n = parseInt(num.charAt(i), 10);
        if (alt) {
          n *= 2;
          if (n > 9) n -= 9;
        }
        sum += n;
        alt = !alt;
      }
      return sum % 10 === 0;
    };

    if (!luhnCheck(id)) return "Invalid ID number checksum";

    return null;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (deceasedDetails.email && !/^\S+@\S+\.\S+$/.test(deceasedDetails.email))
      newErrors.email = "Invalid email format";

    if (!deceasedDetails.title) newErrors.title = "Title is required";

    if (!minimalCapture && !deceasedDetails.initials) newErrors.initials = "Initials are required";

    if (!deceasedDetails.firstName)
      newErrors.firstName = "First name is required";

    if (!deceasedDetails.lastName) newErrors.lastName = "Last name is required";
    if (!deceasedDetails.gender)
      newErrors.gender = "Gender is required";

    if (!minimalCapture && !deceasedDetails.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";
    else if (deceasedDetails.dateOfBirth > todayLocal)
      newErrors.dateOfBirth = "Date of birth cannot be in the future";

    if (!deceasedDetails.dateOfDeath)
      newErrors.dateOfDeath = "Date of death is required";
    else if (deceasedDetails.dateOfDeath > todayLocal)
      newErrors.dateOfDeath = "Date of death cannot be in the future";

    if (
      deceasedDetails.dateOfBirth &&
      deceasedDetails.dateOfDeath &&
      deceasedDetails.dateOfBirth > deceasedDetails.dateOfDeath
    ) {
      newErrors.dateOfBirth = "Date of birth cannot be after date of death";
    }

    if (!deceasedDetails.causeOfDeath)
      newErrors.causeOfDeath = "Cause of death is required";

    if (!minimalCapture && !deceasedDetails.relationship)
      newErrors.relationship = "Relationship is required";

    if (sourceType === "POLICY" && !selectedLivesInsuredOption) {
      newErrors.policyMember = "Please select the lives insured member";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    const isValid = validateForm();

    if (!isValid) return;

    onNext();
  };

  if (validationRef) {
    validationRef.current = validateForm;
  }

  useEffect(() => {
    if (sourceType !== "POLICY" || !selectedLivesInsured) return;

    const historicDeceased = getExistingDeceasedSnapshot(selectedLivesInsured);
    if (historicDeceased) {
      const nextDetails = {
        title: normalizeTitleValue(historicDeceased?.title) || deceasedDetails.title,
        initials: historicDeceased?.initials || deceasedDetails.initials,
        firstName: historicDeceased?.firstName || deceasedDetails.firstName,
        lastName: historicDeceased?.lastName || deceasedDetails.lastName,
        relationship: historicDeceased?.relationship || deceasedDetails.relationship,
        dateOfBirth: formatPolicyDate(historicDeceased?.dateOfBirth) || deceasedDetails.dateOfBirth,
        dateOfDeath: formatPolicyDate(historicDeceased?.dateOfDeath) || deceasedDetails.dateOfDeath,
        identificationNumber: historicDeceased?.identificationNumber || deceasedDetails.identificationNumber,
        identificationType:
          historicDeceased?.identificationType ||
          (historicDeceased?.passportNumber && !historicDeceased?.identificationNumber
            ? "passport"
            : deceasedDetails.identificationType),
        gender: historicDeceased?.gender || deceasedDetails.gender,
        causeOfDeath: historicDeceased?.causeOfDeath || deceasedDetails.causeOfDeath,
      };

      const hasHistoricChanges = Object.entries(nextDetails).some(([key, value]) => {
        const currentValue = (deceasedDetails as Record<string, any>)[key];
        return String(currentValue || "") !== String(value || "");
      });

      if (hasHistoricChanges) {
        setDeceasedDetails(nextDetails);
      }
      return;
    }

    const identifier = getPolicyMemberIdentifier(selectedLivesInsured);
    const passportNumber = getPolicyMemberValue(selectedLivesInsured, [
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]);
    const dateOfBirth = getPolicyMemberValue(selectedLivesInsured, [
      "dateOfBirth",
      "Date_Of_Birth",
      "Date_of_Birth",
      "date_of_birth",
      "BenDOB",
    ]);
    const dateOfDeath = getPolicyMemberValue(selectedLivesInsured, [
      "dateOfDeath",
      "Date_Of_Death",
      "Date_of_Death",
      "date_of_death",
    ]);

    const nextDetails = {
      title:
        normalizeTitleValue(getPolicyMemberValue(selectedLivesInsured, ["title", "Title"])) ||
        deceasedDetails.title,
      initials: getPolicyMemberValue(selectedLivesInsured, ["initials", "Initials"]) || deceasedDetails.initials,
      firstName:
        getPolicyMemberValue(selectedLivesInsured, ["firstName", "name", "First_Name", "Name", "member_name"]) ||
        deceasedDetails.firstName,
      lastName:
        getPolicyMemberValue(selectedLivesInsured, ["lastName", "surname", "Surname"]) ||
        deceasedDetails.lastName,
      relationship:
        getPolicyMemberRelation(
          selectedLivesInsured,
          selectedLivesInsured.__caseLivesInsuredRelation || "",
        ) || deceasedDetails.relationship,
      dateOfBirth: formatPolicyDate(dateOfBirth) || deceasedDetails.dateOfBirth,
      dateOfDeath: formatPolicyDate(dateOfDeath) || deceasedDetails.dateOfDeath,
      identificationNumber: identifier || deceasedDetails.identificationNumber,
      identificationType:
        getPolicyMemberValue(selectedLivesInsured, ["identificationType"]) ||
        (identifier && !passportNumber ? "id" : passportNumber ? "passport" : deceasedDetails.identificationType),
      gender: getPolicyMemberValue(selectedLivesInsured, ["gender", "Gender"]) || deceasedDetails.gender,
    };

    const hasChanges = Object.entries(nextDetails).some(([key, value]) => {
      const currentValue = (deceasedDetails as Record<string, any>)[key];
      return String(currentValue || "") !== String(value || "");
    });

    if (hasChanges) {
      setDeceasedDetails(nextDetails);
    }
  }, [
    deceasedDetails,
    selectedLivesInsured,
    setDeceasedDetails,
    sourceType,
  ]);

  const clearError = (field: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  useEffect(() => {
    if (deceasedDetails.identificationType !== "id") return;

    const parsedDetails = extractSouthAfricanIdDetails(
      deceasedDetails.identificationNumber || "",
    );

    if (!parsedDetails) return;

    const updates: Record<string, string> = {};

    if (deceasedDetails.dateOfBirth !== parsedDetails.dateOfBirth) {
      updates.dateOfBirth = parsedDetails.dateOfBirth;
    }

    if (deceasedDetails.gender !== parsedDetails.gender) {
      updates.gender = parsedDetails.gender;
    }

    if (Object.keys(updates).length > 0) {
      setDeceasedDetails(updates);
    }
  }, [
    deceasedDetails.identificationNumber,
    deceasedDetails.identificationType,
    deceasedDetails.dateOfBirth,
    deceasedDetails.gender,
    setDeceasedDetails,
  ]);

  return (
    <Paper
      elevation={0}
      sx={caseFormCardSx}
    >
      {sourceType === "POLICY" && (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            backgroundColor: "#FCFCFD",
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography fontSize={16} fontWeight={800} color="#111827" textAlign="left">
                Selected Policy Member
              </Typography>
              <Typography fontSize={13} color="#6B7280" mt={0.5} textAlign="left">
                The deceased member was selected on the previous step. These deceased fields are prefilled from that policy member and can still be adjusted here.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography fontSize={13} color="#374151" mt={0.5} textAlign="left">
                Policy Number: {policyNumber || "Not captured"}
              </Typography>
              <Typography fontSize={13} color="#374151" textAlign="left">
                Name: {selectedLivesInsuredOption ? getPolicyMemberName(selectedLivesInsuredOption.member) : "Not captured"}
              </Typography>
              <Typography fontSize={13} color="#374151" textAlign="left">
                Relation:{" "}
                {selectedLivesInsuredOption
                  ? getPolicyMemberRelation(
                      selectedLivesInsuredOption.member,
                      selectedLivesInsuredOption.member.__caseLivesInsuredRelation || "",
                    ) || "Not captured"
                  : "Not captured"}
              </Typography>
              <Typography fontSize={13} color="#374151" textAlign="left">
                Identifier: {selectedLivesInsuredOption ? getPolicyMemberIdentifier(selectedLivesInsuredOption.member) || "Not captured" : "Not captured"}
              </Typography>
              {!selectedLivesInsuredOption && policyLoading && (
                <Typography fontSize={13} color="#6B7280" textAlign="left">
                  Loading policy member details...
                </Typography>
              )}
              {!selectedLivesInsuredOption && !policyLoading && policyError && (
                <Typography fontSize={13} color="error.main" textAlign="left">
                  {policyError}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      <Grid container spacing={2.25} sx={caseGridSx}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            select
            fullWidth
            label="Title"
            variant="outlined"
            value={normalizeTitleValue(deceasedDetails.title || "")}
            onChange={(e: any) => {
              setDeceasedDetails({ title: e.target.value });
              clearError("title");
            }}
            error={Boolean(errors.title)}
            helperText={errors.title}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) =>
                selected ? (
                  selected
                ) : (
                  <span style={{ color: "#aaa" }}>Select Title</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    maxHeight: "300px",
                  },
                },
              },
            }}
          >
            {titleOptions.map((titleOption) => (
              <MenuItem
                key={titleOption}
                value={titleOption}
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {String(titleOption).toUpperCase()}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            fullWidth
            label="Initials"
            placeholder="Initials"
            value={deceasedDetails.initials}
            onChange={(e: any) => {
              setDeceasedDetails({ initials: e.target.value });
              clearError("initials");
            }}
            error={Boolean(errors.initials)}
            helperText={errors.initials}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            fullWidth
            label="First Name"
            placeholder="First Name"
            value={deceasedDetails.firstName}
            onChange={(e: any) => {
              setDeceasedDetails({ firstName: e.target.value });
              clearError("firstName");
            }}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            fullWidth
            label="Last Name"
            placeholder="Last Name"
            value={deceasedDetails.lastName}
            onChange={(e: any) => {
              setDeceasedDetails({ lastName: e.target.value });
              clearError("lastName");
            }}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <RadioGroup
            row
            name="passport"
            value={deceasedDetails.identificationType}
            sx={caseRadioGroupSx}
            onChange={(e) => {
              setDeceasedDetails({ identificationType: e.target.value });
              clearError("identificationType");
            }}
          >
            <FormControlLabel value="id" control={<Radio />} label="ID" />
            <FormControlLabel
              value="passport"
              control={<Radio />}
              label="Passport"
            />
          </RadioGroup>
          <Input
            fullWidth
            variant="outlined"
            placeholder={
              deceasedDetails.identificationType === "passport"
                ? "Passport Number"
                : "ID Number"
            }
            value={deceasedDetails.identificationNumber}
            onChange={(e: any) => {
              const value = e.target.value;
              const updatedFields: Record<string, string> = {
                identificationNumber: value,
              };

              if (deceasedDetails.identificationType === "id") {
                const parsedDetails = extractSouthAfricanIdDetails(value);
                if (parsedDetails) {
                  updatedFields.dateOfBirth = parsedDetails.dateOfBirth;
                  updatedFields.gender = parsedDetails.gender;
                  clearError("dateOfBirth");
                  clearError("gender");
                }
              }

              setDeceasedDetails(updatedFields);
              clearError("identificationNumber");

              if (deceasedDetails.identificationType === "id") {
                const error = validateSouthAfricanID(value);
                setErrors((prev: any) => ({
                  ...prev,
                  identificationNumber: error || "",
                }));
              } else {
                setErrors((prev: any) => {
                  const newErrors = { ...prev };
                  delete newErrors.identificationNumber;
                  return newErrors;
                });
              }
            }}
            error={Boolean(errors.identificationNumber)}
            helperText={errors.identificationNumber}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
          <Input
            fullWidth
            type="email"
            label="Email Address (optional)"
            placeholder="Email Address"
            value={deceasedDetails.email || ""}
            onChange={(e: any) => {
              setDeceasedDetails({ email: e.target.value });
              clearError("email");
            }}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
          <Input
            fullWidth
            label="Phone Number (if applicable)"
            placeholder="Phone Number"
            value={deceasedDetails.contactNumber || ""}
            onChange={(e: any) => setDeceasedDetails({ contactNumber: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
          <Input
            select
            fullWidth
            label="Gender"
            variant="outlined"
            value={deceasedDetails.gender || ""}
            onChange={(e: any) => {
              setDeceasedDetails({ gender: e.target.value });
              clearError("gender");
            }}
            error={Boolean(errors.gender)}
            helperText={errors.gender}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) =>
                selected ? (
                  selected
                ) : (
                  <span style={{ color: "#aaa" }}>Select Gender</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    maxHeight: "300px",
                  },
                },
              },
            }}
          >
            <MenuItem
              value="Male"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Male
            </MenuItem>
            <MenuItem
              value="Female"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Female
            </MenuItem>
            <MenuItem
              value="Other"
              sx={{
                background: "background.default",
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              Other
            </MenuItem>
          </Input>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
          <Input
            fullWidth
            type="date"
            label="Date of Birth"
            InputLabelProps={{ shrink: true }}
            value={deceasedDetails.dateOfBirth}
            onChange={(e: any) => {
              setDeceasedDetails({ dateOfBirth: e.target.value });
              clearError("dateOfBirth");
            }}
            error={Boolean(errors.dateOfBirth)}
            helperText={errors.dateOfBirth}
            inputProps={{
              max: todayLocal,
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} mt={1.8}>
          <Input
            fullWidth
            type="date"
            label="Date of Death"
            InputLabelProps={{ shrink: true }}
            value={deceasedDetails.dateOfDeath}
            onChange={(e: any) => {
              setDeceasedDetails({ dateOfDeath: e.target.value });
              clearError("dateOfDeath");
            }}
            error={Boolean(errors.dateOfDeath)}
            helperText={errors.dateOfDeath}
            inputProps={{
              max: todayLocal,
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            fullWidth
            label="Cause of Death"
            placeholder="Cause of Death"
            InputLabelProps={{ shrink: true }}
            value={deceasedDetails.causeOfDeath}
            onChange={(e: any) => {
              setDeceasedDetails({ causeOfDeath: e.target.value });
              clearError("causeOfDeath");
            }}
            error={Boolean(errors.causeOfDeath)}
            helperText={errors.causeOfDeath}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Input
            select
            fullWidth
            label="Relationship to Informant"
            variant="outlined"
            value={deceasedDetails.relationship}
            onChange={(e: any) => {
              setDeceasedDetails({ relationship: e.target.value });
              clearError("relationship");
            }}
            error={Boolean(errors.relationship)}
            helperText={errors.relationship}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) =>
                selected ? (
                  selected
                ) : (
                  <span style={{ color: "#aaa" }}>Select Relationship</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    maxHeight: "300px",
                  },
                },
              },
            }}
          >
            {loading ? (
              <MenuItem disabled>Loading relationships...</MenuItem>
            ) : (
              relationshipOptions.map((relation: string, index) => (
                <MenuItem
                  key={index}
                  value={relation}
                  sx={{
                    background: "background.default",
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  {relation}
                </MenuItem>
              ))
            )}
          </Input>
        </Grid>
      </Grid>

      {!hideActions && (
        <Box sx={caseActionBarSx}>
          <ButtonComp variant="outlined" sx={caseButtonSx} onClick={onBack}>
            Back
          </ButtonComp>

          <ButtonComp
            variant="contained"
            sx={caseButtonSx}
            onClick={handleNextClick}
          >
            Next
          </ButtonComp>
        </Box>
      )}
    </Paper>
  );
}
